import { useCallback, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { WorkflowTabs, type WorkflowTab } from '@/components/WorkflowTabs'
import { GeneratorFrame, PriceConfirmDialog, StudioPanel, SubmitBar } from '@/components/studio'
import { Badge, Card, CardBody, CardHead, Chips, Field, Input, Segmented, Select, Textarea } from '@/components/ui'
import { ProductAssetPickerButton } from '@/components/ProductAssetPicker'
import { COPY_CHANNELS, COPY_GROUPS, COPY_WRITING_DEFAULTS } from '@/config/catalog'
import { DEFAULT_PLATFORM_ID, FORMAT_LABEL, PLATFORM_GROUPS, getPlatform } from '@/config/platforms'
import { buildCopyFillBrief, buildCopyPrompt, type CopyInput } from '@/config/prompts'
import { DEFAULT_FAMILY_ID } from '@/config/modelFamilies'
import { AiFillButton } from '@/components/AiFillButton'
import { useJobCenter } from '@/context/JobCenterContext'
import { useModels } from '@/context/ModelsContext'
import { FILL_FORM_FIELDS, type ProductAsset } from '@/api/types'
import { useDraft } from '@/hooks/useDraft'
import { useGenerateFlow } from '@/hooks/useGenerateFlow'
import { useLocalStorage } from '@/hooks/useLocalStorage'
import { usePricePreview } from '@/hooks/usePricePreview'
import { useToast } from '@/context/ToastContext'
import { productImportMessage } from '@/lib/productRefs'

/**
 * 文案模型清单**不在本页硬编码**，从 `GET /api/aigc/models` 拉
 * （`useModels().list('text')`），后端 `registry.FAMILIES` 是唯一真源。
 *
 * 上一版这里写了 4 个占位（deepseek-chat / gpt-4o-mini / qwen-plus /
 * moonshot-v1-8k）：3 个后端从来没有过，剩下那个也不在本机 Key 的开放
 * 列表里；页面还挂着「后端未接入」的提示，选了也不生效。
 * 现在真实可用的只有后端下发的 deepseek-flash / deepseek-v4-pro 两个。
 */

/**
 * 平台清单来自平台档案（`config/platforms.ts`），不再是本页手写的字符串数组。
 * 手写的版本只有名字、零规则，还和后端、Banner 页三处对不上。
 * 这里按「国内 / 海外」分组，避免海外平台混在国内名单里被误选。
 */
/*
 * 平台清单按国内外分组 —— 分组本身从 config/platforms 取（PLATFORM_GROUPS），
 * 本页不再手写。手写两份的后果：加平台要改两处，漏一处就静默少一项。
 */

type FormState = CopyInput & { model: string; count: number; skills: boolean }

const DEFAULTS: FormState = {
  channel: 'product',
  productName: '',
  sellingPoints: '',
  audience: '',
  platform: DEFAULT_PLATFORM_ID,
  /*
   * 语气 / 篇幅的默认值取自 `COPY_WRITING_DEFAULTS`（catalog.ts）——
   * `/social-image` 的「图文同出」也要用同一份口径（那一页刻意不给这两个控件）。
   * 写两份字符串的后果见那个常量上的注释。
   */
  tone: COPY_WRITING_DEFAULTS.tone,
  length: COPY_WRITING_DEFAULTS.length,
  extra: '',
  model: DEFAULT_FAMILY_ID.text,
  count: 1,
  /**
   * skill 加载器，**默认关**（用户 2026-09-29 的原话：「正常是不用的，除非我必须要求」）。
   *
   * 关着 = 与加这个能力之前逐字相同：提示词完全由前端拼，一次 POST 出稿。
   * 打开后模型可以先读 SKILL.md 再动笔，代价是慢 3-5 秒、token 高一个量级，
   * 而且**产物不可复现**（同一输入两次可能一次读文档、一次不读）。
   */
  skills: false,
}

/**
 * 三个页签（不是步骤）。
 *
 * 2026-09-28 用户驳回了原来的三步向导，原话：「不需要准备素材」「这个应该 2-3 tab」。
 * 文案页的字段本身就分三块（选渠道+给料 / 定写法 / 出稿），页签能把这三块
 * 一次性摆在眼前横向比较，而向导只让人看到当前那一块、还得按顺序走。
 *
 * 页签 1 的第一个决策仍是渠道 —— 它决定后面所有规则的形态（同一条路由换渠道 =
 * 换工种），所以换渠道仍然会把页面拉回页签 1。
 */
const PANES: WorkflowTab[] = [
  { id: 'channel', title: '渠道与商品', desc: '选子类与渠道，填产品名称、卖点与人群' },
  { id: 'style', title: '写法与平台', desc: '投放平台、语气、篇幅与补充要求' },
  { id: 'generate', title: '生成', desc: '模型、版本数与参数核对' },
]

export function CopyStudio() {
  const [sp, setSp] = useSearchParams()
  const { value: form, patch } = useDraft('copywriting', DEFAULTS)
  const [step, setStep] = useLocalStorage<number>('step_copywriting', 0)
  const { cancel, jobsFor, outputsFor, clearResults } = useJobCenter()
  const flow = useGenerateFlow()
  const toast = useToast()

  /* ------------------------------------------------------------------ *
   * 从商品库带入（2026-10-09）
   *
   * 用户口径：「其他页面也可以引用这个商品库啊」。文案工作台**没有图片槽位**
   * （这一页产出的是文字），所以带入的是标题 + 卖点两样 —— 图带进来没处放。
   * 因此这里不走 `mergeProductRefs`（那是给带图的页面用的），
   * 只复用 `productImportMessage` 保证提示语与其它页同口径。
   *
   * ⚠️ 不新增第二份 state：带入后就是普通的手填值，用户随便改。
   * ------------------------------------------------------------------ */
  const applyProductAsset = useCallback(
    (asset: ProductAsset) => {
      patch({ productName: asset.title, sellingPoints: asset.sellingPoints })
      toast.success(productImportMessage(asset.title))
    },
    [patch, toast],
  )

  // 文案模型从后端拉（唯一真源）。`normalize` 负责把草稿里可能遗留的旧 id
  // （如已下线的 deepseek-chat）纠正到当前可用模型 —— 不纠正的话下拉框会是空白，
  // 用户以为模型被删了。
  const {
    list: listModels,
    normalize: normalizeModel,
    skillsEnabled,
    skillCount,
  } = useModels()
  const llmModels = listModels('text')
  const modelId = normalizeModel(form.model, 'text')

  useEffect(() => {
    if (!llmModels.length) return
    if (modelId !== form.model) patch({ model: modelId })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [llmModels.length, modelId])

  const urlChannel = sp.get('channel')
  const urlGroup = sp.get('group')
  const groupFromUrl = COPY_GROUPS.find((g) => g.id === urlGroup)?.id
  const channel = COPY_CHANNELS.find((c) => c.id === urlChannel)?.id ?? form.channel
  const active = COPY_CHANNELS.find((c) => c.id === channel)!
  /** 子类由渠道推导 —— 不另存一份状态，否则「渠道」和「子类」早晚会对不上 */
  const group = active.group
  const groupChannels = COPY_CHANNELS.filter((c) => c.group === group)
  const profile = getPlatform(form.platform)

  /**
   * 深链两种粒度都要吃：
   *   ?channel=ad-copy  → 精确到渠道
   *   ?group=ad         → 精确到子类，落到该子类第一个渠道
   * 两者同时出现时以 channel 为准（它更具体）。
   */
  useEffect(() => {
    const want =
      COPY_CHANNELS.find((c) => c.id === urlChannel)?.id ??
      (groupFromUrl ? COPY_CHANNELS.find((c) => c.group === groupFromUrl)?.id : undefined)
    if (!want || want === form.channel) return
    patch({ channel: want })
    setStep(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlChannel, groupFromUrl])

  const prompt = useMemo(() => buildCopyPrompt({ ...form, channel }), [form, channel])
  const price = usePricePreview({ appSlug: 'copywriting', model: modelId, outputs: form.count })

  const jobs = jobsFor('copywriting')
  const outputs = outputsFor('copywriting')
  const canSubmit = form.productName.trim().length > 0
  /**
   * 实际是否走 skill 加载器 = **两道开关都亮**。
   *
   * 后端环境关着时（`skillsEnabled` 为 false）即使草稿里存着 true 也不算 ——
   * 草稿可能是上次后端开着时存的，照原样提交会被 400 挡掉，
   * 用户看到「技能加载器未启用」却找不到那个开关（它已经不渲染了）。
   */
  const useSkills = skillsEnabled && form.skills

  const switchChannel = (id: string) => {
    patch({ channel: id })
    setSp({ channel: id }, { replace: true })
    setStep(0)
  }

  /** 切子类 = 落到该子类第一个渠道。子类本身不是可提交状态，它只是渠道的容器。 */
  const switchGroup = (id: string) => {
    const first = COPY_CHANNELS.find((c) => c.group === id)
    if (!first) return
    switchChannel(first.id)
  }

  const doGenerate = () =>
    flow.request({
      info: {
        title: active.name,
        appSlug: 'copywriting',
        model: modelId,
        platform: `${profile.name} · 写作用语 ${profile.copyLanguage}`,
        outputs: form.count,
        refImages: 0,
        prompt,
        price: (price?.estimatedPrice ?? 0) * form.count,
        basis: price?.basis,
      },
      args: {
        appSlug: 'copywriting',
        title: `${active.name} · ${form.productName}`,
        sourcePage: 'copywriting',
        model: modelId,
        platform: form.platform,
        prompt,
        inputs: { ...form, channel, __title: active.name, skills: useSkills },
        outputs: form.count,
        estimatedMs: 12000,
        // 第二道开关。后端环境没开时这里恒为 false，绝不发 true 去撞 400。
        skills: useSkills,
      },
    })

  /* ------------------------- 页签 1 · 素材与配置 ------------------------- */

  /**
   * 「AI 填充」不让点的原因（有值 = 禁用，不隐藏）。
   *
   * 前置条件只有**产品名**一条 —— LLM 是纯文本的（看不了参考图），
   * 没有产品名它只能编，而编出来的卖点看着像真的，用户会一路用到出稿
   * 才发现产品是它造的。填哪几个字段由 `FILL_FORM_FIELDS.copy` 说了算。
   */
  const copyFillBlocked = !form.productName.trim()
    ? '先填产品名称 —— AI 需要知道这是什么产品才写得对'
    : undefined

  const stepAssets = (
    <>
      <Card>
        <CardHead title="文案渠道" hint="先选子类再选渠道；换渠道会回到第一个页签，写作口径完全不同" icon="text" />
        <CardBody>
          {/*
            两级选择：子类（商品 / 社媒 / 广告 / 视频 / SEO / 本地化）→ 渠道。
            14 条渠道铺成一行是信息过载，而且它们是六个不同工种的东西 ——
            做广告文案的人不该在眼前挂着「直播口播稿」。
            子类与侧栏「② 文案」的 6 个入口是同一份配置（COPY_GROUPS）。
          */}
          <Field label="子类" required>
            <Chips
              value={group}
              onChange={switchGroup}
              options={COPY_GROUPS.map((g) => ({ value: g.id, label: g.name }))}
            />
          </Field>
          <Field label="渠道" required>
            <Chips
              value={channel}
              onChange={switchChannel}
              options={groupChannels.map((c) => ({ value: c.id, label: c.name }))}
            />
          </Field>
          <div className="callout">
            <span className="text-xs muted">{active.description}</span>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHead
          title="商品信息"
          icon="box"
          hint="卖点写得越具体，出来的文案越不像模板"
          right={
            /* 「AI 补素材」—— 补的是**写正文要用的料**，不是正文本身。
               正文由用户点「生成」时按渠道的写作口径统一产出；在这里先写一段，
               他要么当草稿留着（和后面生成的撞车），要么删掉（白花钱）。 */
            <AiFillButton<FormState>
              kind="copy"
              fields={FILL_FORM_FIELDS.copy}
              brief={buildCopyFillBrief(form)}
              sourcePage="copywriting"
              /* 快照必须在**覆盖前**取 */
              snapshot={{
                sellingPoints: form.sellingPoints,
                audience: form.audience,
                extra: form.extra,
              }}
              onFill={patch}
              blocked={copyFillBlocked}
              label="AI 补素材"
            />
          }
        />
        <CardBody>
          <Field
            label="产品名称"
            required
            /* 「从商品库选择」—— 商品库录好的档案一次带进来。
               ⚠️ 这一页**只接标题与卖点**：文案工作台没有图片槽位，
               带进来的图无处安放（选择器交回三样，各页按自己的字段接）。 */
            right={<ProductAssetPickerButton onPick={applyProductAsset} />}
          >
            <Input
              value={form.productName}
              placeholder="如：华数云硅藻土沥水垫"
              onChange={(e) => patch({ productName: e.target.value })}
            />
          </Field>
          <Field label="核心卖点">
            <Textarea
              value={form.sellingPoints}
              rows={3}
              placeholder="如：0.5s 吸水、可折叠、耐高温 120℃"
              onChange={(e) => patch({ sellingPoints: e.target.value })}
            />
          </Field>
          <Field label="目标人群">
            <Input
              value={form.audience}
              placeholder="如：25-35 岁租房女性、有宠物的家庭"
              onChange={(e) => patch({ audience: e.target.value })}
            />
          </Field>
        </CardBody>
      </Card>
    </>
  )

  /* ------------------------- 页签 1 · 参数配置 ------------------------- */

  const stepConfig = (
    <Card>
      <CardHead title="投放与写法" icon="globe" hint="换平台会真的换规则，不只是换个标题" />
      <CardBody>
        <Field label="投放平台">
          <Select value={form.platform} onChange={(e) => patch({ platform: e.target.value })}>
            {PLATFORM_GROUPS.map((g) => (
              <optgroup key={g.label} label={g.label}>
                {g.list.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </optgroup>
            ))}
          </Select>
        </Field>
        <div className="callout">
          <span className="text-xs muted">
            {profile.name} · 写作用语 {profile.copyLanguage} · 禁词 {profile.bannedWords.length} 条
          </span>
        </div>
        <Field label="语气">
          <Segmented
            value={form.tone}
            onChange={(v) => patch({ tone: v })}
            options={['真实自然，不吹', '专业理性', '活泼种草'].map((t) => ({ value: t, label: t.slice(0, 4) }))}
          />
        </Field>
        <Field label="篇幅">
          <Segmented
            value={form.length}
            onChange={(v) => patch({ length: v })}
            options={[
              { value: '短（100-200 字）', label: '短' },
              { value: '中等（300-500 字）', label: '中' },
              { value: '长（800 字以上）', label: '长' },
            ]}
          />
        </Field>
        <Field label="补充要求（可选）">
          <Textarea value={form.extra} rows={2} onChange={(e) => patch({ extra: e.target.value })} />
        </Field>
      </CardBody>
    </Card>
  )

  /* ------------------------- 页签 2 · 核对与生成 ------------------------- */

  const stepGenerate = (
    <>
      <Card>
        <CardHead title="模型与版本" icon="bolt" hint="也可在「设置」里接自己的 OpenAI 兼容接口" />
        <CardBody>
          <Field
            label="文案模型"
            hint={
              llmModels.length
                ? `按条计价 · ${llmModels.find((m) => m.id === modelId)?.note ?? 'DeepSeek 官方直连'}`
                : '模型清单加载中…（后端未就绪时会用内置兜底）'
            }
          >
            <Select value={modelId} onChange={(e) => patch({ model: e.target.value })}>
              {llmModels.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name} · ¥{m.unitPrice.toFixed(2)}/{m.unit}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="生成版本数" hint="一次给多个版本，挑最好的那个改">
            <Segmented
              value={String(form.count)}
              onChange={(v) => patch({ count: Number(v) })}
              options={['1', '2', '3'].map((n) => ({ value: n, label: `${n} 版` }))}
            />
          </Field>
          {/*
            skill 加载器。**只在后端环境开着时才渲染**（没渲染 ≠ 报错）——
            后端关着时这个开关点下去必然 400，放个死控件比不放更费解。
            消费级页面不该出现这个字段，所以文案上也写清「默认不用」。
          */}
          {skillsEnabled ? (
            <Field
              label="技能加载器"
              hint={`默认关闭。开启后模型会先读技能文档再动笔（可从 ${skillCount} 个技能里取）——更慢、更贵，且同一次输入的跑法每次可能不同，只在必须套用大段外部规则时开。`}
            >
              <Segmented
                value={useSkills ? 'on' : 'off'}
                onChange={(v) => patch({ skills: v === 'on' })}
                options={[
                  { value: 'off', label: '关闭' },
                  { value: 'on', label: '开启' },
                ]}
              />
            </Field>
          ) : null}
        </CardBody>
      </Card>

      <Card>
        <CardHead title="参数核对" icon="check" hint="确认无误后提交" />
        <CardBody tight>
          <div className="review-list">
            <div className="review-list__row">
              <span className="review-list__k">渠道</span>
              <span className="review-list__v">{active.name}</span>
            </div>
            <div className="review-list__row">
              <span className="review-list__k">产品</span>
              <span className={`review-list__v${form.productName.trim() ? '' : ' review-list__v--muted'}`}>
                {form.productName.trim() || '未填写'}
              </span>
            </div>
            <div className="review-list__row">
              <span className="review-list__k">卖点</span>
              <span className={`review-list__v${form.sellingPoints.trim() ? '' : ' review-list__v--muted'}`}>
                {form.sellingPoints.trim() ? `${form.sellingPoints.split('\n').filter(Boolean).length} 条` : '未填写'}
              </span>
            </div>
            <div className="review-list__row">
              <span className="review-list__k">人群</span>
              <span className={`review-list__v${form.audience.trim() ? '' : ' review-list__v--muted'}`}>
                {form.audience.trim() || '未填写'}
              </span>
            </div>
            <div className="review-list__row">
              <span className="review-list__k">投放平台</span>
              <span className="review-list__v">
                {profile.name} <span className="muted">· 写作用语 {profile.copyLanguage}</span>
              </span>
            </div>
            <div className="review-list__row">
              <span className="review-list__k">语气 / 篇幅</span>
              <span className="review-list__v">
                {form.tone} · {form.length}
              </span>
            </div>
            <div className="review-list__row">
              <span className="review-list__k">模型</span>
              <span className="review-list__v">{llmModels.find((m) => m.id === modelId)?.name ?? modelId}</span>
            </div>
            <div className="review-list__row">
              <span className="review-list__k">输出</span>
              <span className="review-list__v">{form.count} 个版本</span>
            </div>
            {/* 只有真开着才列这一行。恒列一行「技能加载器：关闭」是纯噪音 */}
            {useSkills ? (
              <div className="review-list__row">
                <span className="review-list__k">技能加载器</span>
                <span className="review-list__v">
                  开启 <span className="muted">· 模型可先读技能文档再动笔</span>
                </span>
              </div>
            ) : null}
          </div>
        </CardBody>
      </Card>
    </>
  )

  const stepBody = [stepAssets, stepConfig, stepGenerate][Math.min(step, PANES.length - 1)]

  /* ------------------------------ 常驻提交栏 ------------------------------ */

  const submitDock = (
    <Card className="studio__dock">
      <CardBody tight>
        <SubmitBar
          price={(price?.estimatedPrice ?? 0) * form.count}
          basis={price?.basis}
          disabled={!canSubmit}
          disabledReason={!canSubmit ? '请先填写产品名称' : undefined}
          label={`生成${active.name}`}
          onGenerate={doGenerate}
        />
      </CardBody>
    </Card>
  )

  return (
    <div className="page page--wide">
      <GeneratorFrame
        form={
          <WorkflowTabs tabs={PANES} current={step} onChange={setStep}>
            {stepBody}
          </WorkflowTabs>
        }
        panel={
          <>
            {submitDock}
            <StudioPanel
              jobs={jobs}
              outputs={outputs}
              onCancel={cancel}
              onClear={clearResults}
              kind="text"
              resultTitle={`${active.name} 结果`}
            >
              <Card>
                <CardHead
                  title="平台写作要点"
                  hint="这些规则会拼进提示词，不是摆设"
                  icon="info"
                  right={<Badge tone="primary">{profile.name}</Badge>}
                />
                <CardBody>
                  <div className="rules__group">渠道规则</div>
                  <ul className="rules">
                    {(
                      {
                        xhs: [
                          '标题带情绪钩子，20 字以内',
                          '正文第一人称真实体验，先说缺点再说优点',
                          '结尾给「适合谁 / 不适合谁」，提高收藏率',
                          '话题标签 4-6 个，混合大词与精准词',
                        ],
                        moments: [
                          '手机端不折叠：正文控制在 3 行以内',
                          '一张图 + 一段话，场景代入再给行动号召',
                          '不刷屏，同一天最多 1-2 条，避免微商体',
                          '不在正文堆参数，参数留给私聊',
                        ],
                        douyin: [
                          '前 3 秒必须出钩子，不做铺垫',
                          '一口一段：按换气点断句，每段 1-2 句，长句一律拆开',
                          '全程口语，不写书面语、不写长定语、不写「赋能」这类行话',
                          '不给绝对化承诺，避免审核风险',
                        ],
                        'social-intl': [
                          '英文写法，按母语者语感断句，禁止中文直译腔（如「性价比很高」→ value for money）',
                          '首行 125 字符内完成钩子，折叠前就要有信息',
                          'hashtag 分平台给：IG 8-12 个混合大词与垂直词，TikTok 走话题挑战式短标签',
                          '不讲参数，讲这个产品出现在什么样的生活里',
                        ],
                        'ad-copy': [
                          '四段必须一次出齐并分别标注：标题 / 正文 / 促销 / CTA',
                          '标题给点击、正文给理由、促销给时限、CTA 给动作 —— 四段各司其职，不许互相复述',
                          '促销价格与门槛必须真实可兑，写不出的优惠宁可不写',
                          'CTA 只给一个动作；同时给两个等于没给',
                        ],
                        'short-script': [
                          '逐镜出表：镜号 / 时长 / 景别 / 画面 / 台词字幕 / 音效',
                          '「画面」要写到拍摄方能执行，禁止「此处展示产品」这类空话',
                          '总时长与每镜时长必须能对上，不平账的脚本等于没写',
                          '分镜表按平台竖版节奏排，前 3 秒单独一镜',
                        ],
                        seo: [
                          '关键词分三层：大词（打曝光）/ 精准词（打转化）/ 长尾词（捡漏），各标搜索意图',
                          '产品描述前 80 字符必须讲清「是什么 + 给谁用」，搜索位只展示这么长',
                          '同一个产品在不同平台搜索词不同，词表按平台分别给，不要一份词表打天下',
                          '不堆砌无关热词 —— 点击率会掉，且部分平台判违规',
                        ],
                        localization: [
                          '不是逐字译：按目标国语言习惯重写表达与句式',
                          '计量单位、尺码体系、货币必须本地化（英寸↔厘米、磅↔公斤）',
                          '当地节日与场景引用要真实存在，不做「中式英语式」的节日套用',
                          '译文之外标出「直译会出错、我改成了什么」，方便人工复核',
                        ],
                        title: [
                          '核心词尽量前置在前 12 字内',
                          '属性词 4-6 个：材质/尺寸/场景/人群',
                          '避免「最」「第一」「国家级」等极限词',
                          '字符数控制在平台上限的 80% 左右',
                        ],
                        product: [
                          '先列核心卖点，再给详情页模块结构',
                          '每个卖点对应一个用户痛点，不做无痛点的罗列',
                          '参数模块必须真实，不确定的留空让人工补',
                          '不编造检测报告与认证',
                        ],
                        'selling-point': [
                          '每条卖点写成「用户痛点 → 产品事实」两段，不做形容词堆砌',
                          '一条只讲一个购买理由，讲不清就说明它其实不是卖点',
                          '区分「参数」与「卖点」：0.5 秒吸水是参数，洗碗不用等是卖点',
                          '产品资料里没有的功能一律不写，宁缺勿编',
                        ],
                        'detail-copy': [
                          '首屏只讲一件事：这个产品解决什么问题',
                          '模块顺序决定长图怎么拆，模块标题 ≤ 12 字',
                          '参数模块必须真实可核，不确定的标「待补」不猜',
                          '信任背书只用真实的资质与售后政策，不编造销量与检测',
                        ],
                        'main-keywords': [
                          '每条 4-8 字，图上排得下、手机端 3 秒能读完',
                          '一个词只承担一个信息点，不写复合长句',
                          '优先写用户会搜的词，其次才是自夸的词',
                          '不带标点、不带语气词、不带极限词',
                        ],
                        'live-script': [
                          '只写直播口播稿；短视频脚本走「短视频脚本 · 分镜表」渠道，不要混成一份四不像',
                          '开场 3 秒抛问题或结果，不做自我介绍',
                          '一段一个信息点，段落之间留出互动口（扣 1 / 想要 / 还有没有）',
                          '异议处理放在逼单之前：贵、怕不好用、尺寸不合适',
                          '所有功效与优惠必须真实可兑，直播话术没有灰色空间',
                        ],
                      } as Record<string, string[]>
                    )[channel]?.map((r) => (
                      <li key={r} className="rules__item">
                        <span className="rules__dot" />
                        {r}
                      </li>
                    ))}
                  </ul>

                  {/* 这一段是「平台差异」的可视化证据：换平台，这些行会真的变 */}
                  <div className="rules__group">平台档案 · {profile.region}</div>
                  <ul className="rules">
                    <li className="rules__item">
                      <span className="rules__dot" />
                      写作用语：{profile.copyLanguage}
                    </li>
                    <li className="rules__item">
                      <span className="rules__dot" />
                      卖点顺序：{profile.sellingPointFramework.order.join(' > ')}
                    </li>
                    <li className="rules__item">
                      <span className="rules__dot" />
                      表达格式：{FORMAT_LABEL[profile.sellingPointFramework.format]}
                    </li>
                    {profile.sellingPointFramework.limit ? (
                      <li className="rules__item">
                        <span className="rules__dot" />
                        硬限制：{profile.sellingPointFramework.limit}
                      </li>
                    ) : null}
                    <li className="rules__item">
                      <span className="rules__dot" />
                      平台风格：{profile.styleKeywords.join('、')}
                    </li>
                    <li className="rules__item">
                      <span className="rules__dot" />
                      合规禁词（{profile.bannedWords.length} 条）：
                      {profile.bannedWords.slice(0, 6).join('、')}
                      {profile.bannedWords.length > 6 ? ' …' : ''}
                    </li>
                  </ul>
                </CardBody>
              </Card>
            </StudioPanel>
          </>
        }
      />

      <PriceConfirmDialog info={flow.pending} onCancel={flow.cancel} onConfirm={flow.confirm} />
    </div>
  )
}
